const MIN_QUERY_LENGTH = 2;

export default function globalSearch(resultsUrl = '') {
    return {
        isOverlayOpen: false,
        highlightedIndex: -1,
        activeView: 'results',
        resultsUrl,
        orderTab: null,

        get hasEnoughCharacters() {
            return (this.$wire.query || '').trim().length >= MIN_QUERY_LENGTH;
        },

        get navigableEntryCount() {
            return this.$refs.listbox ? this.$refs.listbox.querySelectorAll('[role="option"]').length : 0;
        },

        get enterKeyLabel() {
            if (this.highlightedIndex < 0) {
                return 'see all results';
            }

            const option = this.optionAt(this.highlightedIndex);

            if (!option) {
                return 'see all results';
            }

            if (option.dataset.kind === 'seeAllResults') {
                return 'see all results';
            }

            if (option.dataset.kind === 'seeAllOfType') {
                return `see all ${option.dataset.type}`;
            }

            if (option.dataset.type === 'orders') {
                return 'open order in a new tab';
            }

            return 'open contact card';
        },

        openOverlay(focusField = true) {
            this.isOverlayOpen = true;
            this.activeView = 'results';
            document.body.style.overflow = 'hidden';

            if (focusField) {
                this.$nextTick(() => this.$refs.searchField && this.$refs.searchField.focus());
            }
        },

        closeOverlay() {
            this.isOverlayOpen = false;
            this.highlightedIndex = -1;
            this.activeView = 'results';
            document.body.style.overflow = '';
            this.$wire.set('query', '');
            this.$wire.set('showContactCard', false);
            this.$wire.set('contactCardCustomerId', null);
        },


        toggleOverlay() {
            this.isOverlayOpen ? this.closeOverlay() : this.openOverlay();
        },

        handleEscapeKey() {
            if (this.$wire.showContactCard) {
                this.$wire.leaveContactCard().then(() => {
                    this.$nextTick(() => this.$refs.searchField && this.$refs.searchField.focus());
                });
            } else {
                this.closeOverlay();
            }
        },

        moveHighlight(step) {
            const count = this.navigableEntryCount;

            if (!count) {
                return;
            }

            if (this.highlightedIndex === -1) {
                this.highlightedIndex = step > 0 ? 0 : count - 1;
            } else {
                this.highlightedIndex += step;

                if (this.highlightedIndex < 0) {
                    this.highlightedIndex = -1;
                }

                if (this.highlightedIndex > count - 1) {
                    this.highlightedIndex = 0;
                }
            }

            if (this.highlightedIndex >= 0) {
                this.$nextTick(() => {
                    const option = this.optionAt(this.highlightedIndex);

                    if (option) {
                        option.scrollIntoView({ block: 'nearest' });
                    }
                });
            }
        },

        handleEnterKey() {
            if (this.highlightedIndex < 0) {
                if (this.hasEnoughCharacters) {
                    this.openResultsPage();
                }

                return;
            }

            this.activateNavigableEntry(this.highlightedIndex);
        },

        activateNavigableEntry(index) {
            const option = this.optionAt(index);

            if (!option) {
                return;
            }

            const { kind, type, recordId, url } = option.dataset;

            if (kind === 'seeAllResults') {
                this.openResultsPage();

                return;
            }

            if (kind === 'seeAllOfType') {
                this.openResultsPage(type);

                return;
            }

            if (type === 'orders') {
                this.openOrder(recordId);

                return;
            }

            this.openContactCard(recordId);
        },

        openOrder(orderId) {
            if (!orderId) {
                return;
            }

            this.orderTab = window.open('', '_blank');
            this.$wire.openOrder(orderId);
        },

        handleOrderTab(url) {
            if (!url) {
                if (this.orderTab) {
                    this.orderTab.close();
                }

                this.orderTab = null;

                return;
            }

            if (this.orderTab) {
                this.orderTab.location = url;
                this.orderTab = null;

                return;
            }

            window.open(url, '_blank');
        },

        openContactCard(customerId) {
            if (!customerId) {
                return;
            }

            this.$wire.openContactCard(customerId);
        },

        openContactCardFromResultsPage(event) {
            if (!event.detail || !event.detail.customerId) {
                return;
            }

            this.openOverlay(false);
            this.$wire.openContactCard(event.detail.customerId, true);
        },

        openResultsPage(type = null) {
            const query = (this.$wire.query || '').trim();

            if (query.length < MIN_QUERY_LENGTH || !this.resultsUrl) {
                return;
            }

            const params = new URLSearchParams({ q: query });

            if (type) {
                params.set('type', type);
            }

            if (this.$wire.currentMainEventId) {
                params.set('mainEvent', this.$wire.currentMainEventId);
                params.set('scope', this.$wire.scope === 'event' ? 'event' : 'global');
            }

            window.location.href = `${this.resultsUrl}?${params.toString()}`;
        },

        optionAt(index) {
            return this.$refs.listbox ? this.$refs.listbox.querySelector(`#search-option-${index}`) : null;
        },

        keepFocusInsideOverlay(event) {
            const focusable = [...this.$refs.overlayPanel.querySelectorAll('input, select, button:not([tabindex="-1"])')]
                .filter((element) => element.offsetParent !== null);

            if (!focusable.length) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const current = document.activeElement;

            if (event.shiftKey && (current === first || !this.$refs.overlayPanel.contains(current))) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && current === last) {
                event.preventDefault();
                first.focus();
            }
        },
    };
}
